import { useTranslation } from 'react-i18next';
import { Plus, Shuffle, Trash2 } from 'lucide-react';
import { ColorInput } from '@/components/ColorInput';
import { Button } from '@/components/ui/button';
import { generateRandomHarmoniousPalette } from '@/lib/color/generateHarmoniousPalette';
import {
  buildPaletteColorInputPresets,
  type PaletteEditorValue,
} from '@/lib/color/paletteEditorValue';

const DEFAULT_NEW_COLOR = '#cccccc';

export interface PaletteEditorChangeOptions {
  relinkSubtitle?: boolean;
}

export interface PaletteEditorProps {
  value: PaletteEditorValue;
  onChange: (value: PaletteEditorValue, options?: PaletteEditorChangeOptions) => void;
}

export const PaletteEditor = ({ value, onChange }: PaletteEditorProps) => {
  const { t } = useTranslation();
  const { colors, fontColor } = value;
  const highlightColor = colors[0] ?? DEFAULT_NEW_COLOR;
  const accentColors = colors.slice(1);
  const colorInputPresets = buildPaletteColorInputPresets(value);

  const emitChange = (
    next: PaletteEditorValue,
    options?: PaletteEditorChangeOptions,
  ) => {
    onChange(next, options);
  };

  const updateHighlight = (hex: string) => {
    emitChange({ ...value, colors: [hex, ...accentColors] });
  };

  const updateFontColor = (hex: string) => {
    emitChange({ ...value, fontColor: hex });
  };

  const updateAccent = (index: number, hex: string) => {
    const nextAccents = accentColors.map((color, i) => (i === index ? hex : color));
    emitChange({ ...value, colors: [highlightColor, ...nextAccents] });
  };

  const removeAccent = (index: number) => {
    emitChange({
      ...value,
      colors: [highlightColor, ...accentColors.filter((_, i) => i !== index)],
    });
  };

  const addColor = () => {
    emitChange({ ...value, colors: [...colors, DEFAULT_NEW_COLOR] });
  };

  const randomizePalette = () => {
    const result = generateRandomHarmoniousPalette();
    emitChange(
      { colors: result.colors, fontColor: result.fontColor },
      { relinkSubtitle: true },
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card mb-4 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold">{t('colorInput.palette')}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          data-testid="palette-randomize"
          onClick={randomizePalette}
        >
          <Shuffle className="mr-1 h-3.5 w-3.5" />
          {t('paletteEditor.randomize')}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('paletteEditor.highlightRole')}
          </span>
          <ColorInput
            value={highlightColor}
            onChange={updateHighlight}
            testId="palette-highlight-color"
            presets={colorInputPresets}
            className="h-10 w-full rounded-md"
          />
        </div>
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('paletteEditor.fontColorRole')}
          </span>
          <ColorInput
            value={fontColor}
            onChange={updateFontColor}
            testId="palette-font-color"
            presets={colorInputPresets}
            className="h-10 w-full rounded-md"
          />
        </div>
      </div>

      {accentColors.length > 0 && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('paletteEditor.accentColors')}
          </span>
          <div className="flex flex-wrap gap-2">
            {accentColors.map((color, i) => (
              <div key={i} className="group relative">
                <ColorInput
                  value={color}
                  onChange={(hex) => updateAccent(i, hex)}
                  testId={`palette-accent-${i}`}
                  presets={colorInputPresets}
                  className="h-10 w-12 shrink-0 rounded-md"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute -right-1 -top-1 h-5 w-5 rounded-full opacity-90 shadow-sm group-hover:opacity-100"
                  data-testid={`palette-remove-${i}`}
                  aria-label={t('paletteEditor.removeColor')}
                  onClick={() => removeAccent(i)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="palette-add"
        onClick={addColor}
      >
        <Plus className="mr-1 h-3.5 w-3.5" />
        {t('paletteEditor.addColor')}
      </Button>
    </div>
  );
};
